import { IMAGES } from "@/lib/images";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart, Leaf, ShieldCheck, Users, Sparkles, MapPin } from "lucide-react";

const Overline = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FA38C]">{children}</span>
);

const team = [
  { name: "Maria Alvarez", role: "Founder & Cleaning Lead", initials: "MA" },
  { name: "Daniel Okafor", role: "Head of Landscaping", initials: "DO" },
  { name: "Grace Lin", role: "Client Experience", initials: "GL" },
  { name: "Tom Reyes", role: "Operations Manager", initials: "TR" },
];

export default function About() {
  const navigate = useNavigate();
  return (
    <div className="bg-[#FAFAFA]">
      <section className="relative py-28 md:py-36">
        <div className="absolute inset-0">
          <img src={IMAGES.cleaningCrew} alt="Our crew" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0E1F18]/85 to-[#0E1F18]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Overline>Our Story</Overline>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight">
              Family-built. Detail-obsessed.
            </h1>
            <p className="mt-5 text-white/85 text-lg font-light">
              We help families and businesses across Calgary, Airdrie and Okotoks reclaim their time with pristine homes and breathtaking landscapes.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Overline>Where it began</Overline>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold text-[#1A362D] tracking-tight">From one truck to a trusted name</h2>
          <p className="mt-5 text-[#4A5D4E] font-light leading-relaxed">
            Master Touch began with a single truck, a ladder, and a simple promise: treat every property like it's our own. What started as a neighborhood cleaning service grew into a full-service cleaning and landscaping company — built entirely on referrals and repeat clients across Calgary, Airdrie and Okotoks.
          </p>
          <p className="mt-4 text-[#4A5D4E] font-light leading-relaxed">
            Today we blend old-fashioned craftsmanship with modern tools — including our AI Landscape Visualizer — so you can see your transformation before it happens.
          </p>
          <Button onClick={() => navigate("/booking")} className="mt-7 bg-[#1A362D] hover:bg-[#254A3E] text-white rounded-full px-8">
            Work with us
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-5">
          {[
            { icon: Heart, t: "Care First", d: "We treat your space like family." },
            { icon: ShieldCheck, t: "Integrity", d: "Honest pricing, no surprises." },
            { icon: Leaf, t: "Sustainability", d: "Eco-friendly by default." },
            { icon: Sparkles, t: "Excellence", d: "Detail in every finish." },
          ].map((v) => (
            <div key={v.t} className="p-6 rounded-xl bg-white border border-[#E2E8E4]/60">
              <v.icon className="h-7 w-7 text-[#C47B57]" />
              <h3 className="mt-3 font-heading text-xl font-semibold text-[#1A362D]">{v.t}</h3>
              <p className="mt-1 text-sm text-[#4A5D4E] font-light">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#1A362D] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: "Local", l: "Alberta owned" },
            { n: "12k+", l: "Jobs completed" },
            { n: "4.9★", l: "Average rating" },
            { n: "98%", l: "Repeat clients" },
          ].map((s) => (
            <div key={s.l}>
              <p className="font-heading text-4xl md:text-5xl font-semibold text-[#C47B57]">{s.n}</p>
              <p className="mt-2 text-sm text-[#8FA38C] uppercase tracking-wider">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="text-center max-w-2xl mx-auto">
          <Overline>The people</Overline>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold text-[#1A362D] tracking-tight">Meet the crew</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
          {team.map((m) => (
            <div key={m.name} className="text-center p-8 rounded-xl bg-white border border-[#E2E8E4]/60">
              <div className="mx-auto h-20 w-20 rounded-full bg-[#F2F4F2] flex items-center justify-center font-heading text-2xl font-semibold text-[#1A362D]">
                {m.initials}
              </div>
              <h3 className="mt-4 font-heading text-xl font-semibold text-[#1A362D]">{m.name}</h3>
              <p className="text-sm text-[#8FA38C]">{m.role}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Service Area */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="rounded-3xl bg-white border border-[#E2E8E4]/60 p-10 md:p-14 flex flex-col lg:flex-row gap-10 items-center">
          <div className="flex-1">
            <Overline>Service Area</Overline>
            <h2 className="mt-4 font-heading text-3xl font-semibold text-[#1A362D] tracking-tight">Proudly serving Calgary & area</h2>
            <p className="mt-4 text-[#4A5D4E] font-light">
              We cover Calgary, Airdrie and Okotoks, Alberta — with more communities added every season.
            </p>
            <div className="mt-6 flex items-center gap-2 text-[#8FA38C]">
              <Users className="h-5 w-5" />
              <span className="text-sm">Don't see your area? Reach out — we may already cover it.</span>
            </div>
          </div>
          <div className="flex-1 w-full aspect-[16/10] rounded-2xl bg-[#F2F4F2] flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-30" style={{ backgroundImage: `url(${IMAGES.earthyBg})`, backgroundSize: "cover" }} />
            <div className="relative text-center">
              <MapPin className="h-12 w-12 text-[#C47B57] mx-auto" />
              <p className="mt-3 font-heading text-2xl text-[#1A362D]">Calgary · Airdrie · Okotoks</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
